import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  useMediaQuery,
  Link,
  Alert,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLogin, setManagerDetails } from "state";
import { clearDashboardFetchCache } from "services/dashboardFetch";
import { baseURL } from "services/API";
import LinearProgress from "@mui/material/LinearProgress";
import ForgotPasswordForm from "./ForgotPasswordForm";
import { applyStaffLoginResponse } from "./staffAuth";

const loginSchema = yup.object().shape({
  username: yup.string().required("required"),
  password: yup.string().required("required"),
});

const initialValuesLogin = {
  username: "",
  password: "",
};

const Form = () => {
  const [view, setView] = useState("login");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const login = async (values, onSubmitProps) => {
    setIsLoading(true);
    try {
      const loggedInResponse = await fetch(`${baseURL}/accounts/authenticate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      const loggedIn = await loggedInResponse.json();
      onSubmitProps.resetForm();

      const ok = applyStaffLoginResponse({
        responseBody: loggedIn,
        dispatch,
        navigate,
        setLogin,
        setManagerDetails,
        clearDashboardFetchCache,
        onDenied: () => {
          setAlertMessage(
            "Access denied. This app is for Admin, Property Manager, Relationship Manager, or Broker only."
          );
          setShowAlert(true);
        },
        onFailure: () => {
          setAlertMessage("Invalid username or password. Please try again.");
          setShowAlert(true);
        },
      });
      if (!ok) setTimeout(() => setShowAlert(false), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  if (view === "forgot") {
    return <ForgotPasswordForm onBackToLogin={() => setView("login")} />;
  }

  return (
    <Formik
      onSubmit={login}
      initialValues={initialValuesLogin}
      validationSchema={loginSchema}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        handleSubmit,
      }) => (
        <form onSubmit={handleSubmit}>
          {showAlert && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2,
                borderRadius: "2rem",
                backgroundColor: "#ffebee",
                color: "#c62828"
              }}
            >
              {alertMessage}
            </Alert>
          )}
          
          <Box
            display="grid"
            gap="30px"
            gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            sx={{
              "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
            }}
          >
            <TextField
              label="Username"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.username}
              name="username"
              error={Boolean(touched.username) && Boolean(errors.username)}
              helperText={touched.username && errors.username}
              sx={{ gridColumn: "span 4", borderRadius: "2rem"}}
            />
            <TextField
              label="Password"
              type="password"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.password}
              name="password"
              error={Boolean(touched.password) && Boolean(errors.password)}
              helperText={touched.password && errors.password}
              sx={{ gridColumn: "span 4", borderRadius: "2rem"}}
            />
          </Box>

          {/* BUTTONS */}
          <Box>
            {isLoading ? (
              <LinearProgress 
              color="warning"
              sx={{
                m: "2rem 0",
                p: "1rem",
                backgroundColor: "#141b2d",
                borderRadius: "2rem",
              }}
              />
            ) : (
              <Button
                fullWidth
                type="submit"
                sx={{
                  m: "2rem 0",
                  p: "1rem",
                  borderRadius: "2rem",
                  backgroundColor: "#141b2d",
                  color: "#FCB200",
                  "&:hover": { color: "#141b2d" },
                }}
              >
                LOGIN
              </Button>
            )}
            <Box textAlign="center" mt={1}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => {
                  setShowAlert(false);
                  setView("forgot");
                }}
              >
                Forgot password?
              </Link>
            </Box>
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default Form;
